'use client';

import { useState } from 'react';
import { Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  EmptyMedia,
  EmptyState,
  EmptyStateHeading,
  EmptyStateText,
} from '@kit/ui/empty-state';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kit/ui/select';
import { cn } from '@kit/ui/utils';

import type {
  MeetingStatus,
  MeetingWithDetails,
} from '../../services/meeting.service';
import { MeetingCard } from './meeting-card';

interface MeetingListProps {
  meetings: MeetingWithDetails[];
  onMeetingClick?: (meeting: MeetingWithDetails) => void;
  onConfirm?: (meetingId: string) => void;
  onCancel?: (meetingId: string) => void;
  onComplete?: (meetingId: string) => void;
  viewAs?: 'organization' | 'dancer';
  showFilter?: boolean;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
  className?: string;
}

/**
 * List component for displaying meetings with optional filtering
 *
 * Features:
 * - Status filter dropdown
 * - Sorted by scheduled date
 * - Empty state when no meetings
 * - Grouped by upcoming/past
 */
export function MeetingList({
  meetings,
  onMeetingClick,
  onConfirm,
  onCancel,
  onComplete,
  viewAs = 'organization',
  showFilter = true,
  emptyStateTitle,
  emptyStateDescription,
  className,
}: MeetingListProps) {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState<MeetingStatus | 'all'>(
    'all',
  );

  const filteredMeetings =
    statusFilter === 'all'
      ? meetings
      : meetings.filter((m) => m.status === statusFilter);

  const now = new Date();
  const upcomingMeetings = filteredMeetings
    .filter((m) => new Date(m.scheduled_at) >= now)
    .sort(
      (a, b) =>
        new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime(),
    );
  const pastMeetings = filteredMeetings
    .filter((m) => new Date(m.scheduled_at) < now)
    .sort(
      (a, b) =>
        new Date(b.scheduled_at).getTime() - new Date(a.scheduled_at).getTime(),
    );

  if (meetings.length === 0) {
    return (
      <EmptyState>
        <EmptyMedia>
          <Calendar className="h-12 w-12" />
        </EmptyMedia>
        <EmptyStateHeading>
          {emptyStateTitle ?? t('profiles:meetings.empty.title')}
        </EmptyStateHeading>
        <EmptyStateText>
          {emptyStateDescription ?? t('profiles:meetings.empty.description')}
        </EmptyStateText>
      </EmptyState>
    );
  }

  return (
    <div className={cn('space-y-6', className)}>
      {showFilter && (
        <div className="flex justify-end">
          <Select
            value={statusFilter}
            onValueChange={(value) =>
              setStatusFilter(value as MeetingStatus | 'all')
            }
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder={t('profiles:meetings.filter.status')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {t('profiles:meetings.filter.all')}
              </SelectItem>
              <SelectItem value="pending">
                {t('profiles:meetings.status.pending')}
              </SelectItem>
              <SelectItem value="confirmed">
                {t('profiles:meetings.status.confirmed')}
              </SelectItem>
              <SelectItem value="completed">
                {t('profiles:meetings.status.completed')}
              </SelectItem>
              <SelectItem value="cancelled">
                {t('profiles:meetings.status.cancelled')}
              </SelectItem>
              <SelectItem value="no_show">
                {t('profiles:meetings.status.noShow')}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {filteredMeetings.length === 0 ? (
        <EmptyState>
          <EmptyMedia>
            <Calendar className="h-12 w-12" />
          </EmptyMedia>
          <EmptyStateHeading>
            {t('profiles:meetings.noResults.title')}
          </EmptyStateHeading>
          <EmptyStateText>
            {t('profiles:meetings.noResults.description')}
          </EmptyStateText>
        </EmptyState>
      ) : (
        <>
          {/* Upcoming Meetings */}
          {upcomingMeetings.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-muted-foreground text-sm font-medium tracking-wide uppercase">
                {t('profiles:meetings.sections.upcoming')} (
                {upcomingMeetings.length})
              </h3>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {upcomingMeetings.map((meeting) => (
                  <MeetingCard
                    key={meeting.id}
                    meeting={meeting}
                    onClick={() => onMeetingClick?.(meeting)}
                    onConfirm={
                      onConfirm ? () => onConfirm(meeting.id) : undefined
                    }
                    onCancel={onCancel ? () => onCancel(meeting.id) : undefined}
                    onComplete={
                      onComplete ? () => onComplete(meeting.id) : undefined
                    }
                    viewAs={viewAs}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Past Meetings */}
          {pastMeetings.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-muted-foreground text-sm font-medium tracking-wide uppercase">
                {t('profiles:meetings.sections.past')} ({pastMeetings.length})
              </h3>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {pastMeetings.map((meeting) => (
                  <MeetingCard
                    key={meeting.id}
                    meeting={meeting}
                    onClick={() => onMeetingClick?.(meeting)}
                    onComplete={
                      onComplete ? () => onComplete(meeting.id) : undefined
                    }
                    viewAs={viewAs}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
